import {
	Container,
	Heading,
	Paragraph,
	Skeleton,
	Text,
} from '@audio-network/componentlibrary'
import { Button } from '@audio-network/componentlibrary'
import { Info, Sparkle } from '@audio-network/componentlibrary/icons'
import { createInfiniteHitsSessionStorageCache } from 'instantsearch.js/es/lib/infiniteHitsCache'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import {
	useInfiniteHits,
	useInstantSearch,
	useSearchBox,
	useStats,
	useToggleRefinement,
} from 'react-instantsearch'
import { useSortBy } from 'react-instantsearch'

import { useRefinements } from '@/components/pages/search/CurrentRefinements/CurrentRefinements.hooks'
import { NoResultsPromptSearch } from '@/components/pages/search/Results'
import { mapTracksForAiSearch } from '@/components/pages/search/Results/Results.utils'
import TrackList from '@/components/shared/TrackList/TrackList'
import TrackListSkeleton from '@/components/shared/TrackList/TrackList.skeleton'
import { isMostRecentSort, SORT_OPTIONS } from '@/constants/search'
import { SEARCH } from '@/constants/trackSources'
import { usePersistSearchScrollPosition } from '@/pages/track/searchkeyword/index.hooks'
import useAiSearchStore from '@/stores/aiSearchStore'
import { formatResultsCount } from '@/utils/formatting/formatResultsCount'

import styles from './TrackResults.module.css'

const sessionStorageCache = createInfiniteHitsSessionStorageCache()

const SEARCH_DOWNLOAD_NAME = 'ANW_Search_Results'

const TrackResults = ({
	playlistTracks,
	user,
}: {
	playlistTracks: Track[]
	user: User
}): JSX.Element => {
	const { status } = useInstantSearch()
	const { nbHits, page, hitsPerPage } = useStats()
	const { isLastPage, items, showMore } = useInfiniteHits({
		cache: sessionStorageCache,
	})

	const { containerRef } = usePersistSearchScrollPosition()

	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [isEmptySearch, setIsEmptySearch] = useState(true)
	const [hasSearched, setHasSearched] = useState(false)
	const [showNoResultsPromptSearch, setShowNoResultsPromptSearch] =
		useState(false)
	const { t } = useTranslation(['search'])

	const { value: instrumentalUiToggle } = useToggleRefinement({
		attribute: 'Instrumental',
	})
	const { refinements } = useRefinements()
	const { query } = useSearchBox()

	const {
		aiSearchPending,
		searchByPrompt,
		aiSearchResults,
		aiSearchHasFailed,
	} = useAiSearchStore()

	const isInitialLoading =
		!isLoadingMore && (status === 'stalled' || status === 'loading')

	const aiResultsNoSearchHits =
		!aiSearchHasFailed && aiSearchResults && nbHits === 0

	const areAiResultsOutOfSync =
		aiSearchResults &&
		items.length > 0 &&
		!items.every((item) =>
			aiSearchResults.some((result) => item.Id === Number(result.id))
		)

	const { currentRefinement } = useSortBy(SORT_OPTIONS)

	const showSkeleton =
		isInitialLoading || aiSearchPending || areAiResultsOutOfSync

	const handleShowMore = (): void => {
		setIsLoadingMore(true)

		showMore()
	}

	const tooltipContent = (
		<div className={styles.tooltipContent}>
			<div className={styles.tooltipTitle}>
				<Sparkle className={styles.tooltipIcon} />
				<Text bold>{t('prompt-search-tooltip-title')}</Text>
			</div>
			<Text variant="bodyS">{t('prompt-search-tooltip-description')}</Text>
		</div>
	)

	useEffect(() => {
		if (status === 'idle') {
			setIsLoadingMore(false)
		}
	}, [status])

	useEffect(() => {
		// We set isEmptySearch to true when the instrumental search toggle is not selected and we have no other refinements
		setIsEmptySearch(
			!instrumentalUiToggle.isRefined && refinements.length === 0
		)
	}, [refinements, instrumentalUiToggle])

	useEffect(() => {
		// We set hasSearched to true when the search is loading and user has not searched yet
		if (status === 'loading' && !hasSearched) {
			setHasSearched(true)
		}
	}, [hasSearched, status])

	useEffect(() => {
		const hasKeywordSearchRefinement = refinements.some(
			(refinement) => refinement.isSearchByPrompt === false
		)

		if (user && hasKeywordSearchRefinement) {
			setShowNoResultsPromptSearch(true)
		} else {
			setShowNoResultsPromptSearch(false)
		}
	}, [refinements, user])

	const shouldUseAiSearchRanking =
		aiSearchResults && !isMostRecentSort(currentRefinement)

	const mappedTrackResults = items?.length
		? mapTracksForAiSearch(
				/**
				 * We slice the array by page size because the default behaviour of InstantSearch
				 * means that loading more (increasing page number) and hitting the back button
				 * does not remove further results
				 */
				items?.slice(0, (page + 1) * hitsPerPage),
				shouldUseAiSearchRanking ? aiSearchResults : null
		  )
		: []

	return (
		<Container maxWidth="large" ref={containerRef}>
			{showSkeleton && (
				<>
					<Heading level={2} size="S">
						<Skeleton data-testid="skeleton" width={192} />
					</Heading>
					<TrackListSkeleton count={10} />
				</>
			)}
			{(nbHits === 0 || aiSearchHasFailed || aiResultsNoSearchHits) &&
				!showSkeleton &&
				hasSearched && (
					<>
						<Paragraph className={styles.noResults} align="center">
							{t('no-results')}
						</Paragraph>
						{showNoResultsPromptSearch && (
							<NoResultsPromptSearch query={query} />
						)}
					</>
				)}
			{!showSkeleton && nbHits !== 0 && !aiSearchHasFailed && (
				<>
					<div className={styles.trackCountWrapper}>
						{searchByPrompt && (
							<div className={styles.aiIcon} data-testid="prompt-search-icon">
								<Sparkle color="white" />
							</div>
						)}
						<Heading level={2} size="S" uppercase>
							{isEmptySearch ? (
								t('latest-tracks')
							) : (
								<>
									{t('tracks')}{' '}
									<span
										className={styles.trackCount}
										data-testid="results-count"
									>
										{formatResultsCount(nbHits)}
									</span>
								</>
							)}
						</Heading>
						{searchByPrompt && (
							<Info
								className={styles.promptSearchInfoIcon}
								data-testid="prompt-search-info-icon"
								title={tooltipContent}
							/>
						)}
					</div>
					<TrackList
						download={{ canDownloadAll: true, name: SEARCH_DOWNLOAD_NAME }}
						tracks={isEmptySearch ? playlistTracks : mappedTrackResults}
						source={SEARCH}
					/>
					{isLoadingMore && (
						<>
							<TrackListSkeleton count={16} />
						</>
					)}
					{!isEmptySearch && !isLastPage && !isLoadingMore && (
						<Button className={styles.showMore} onClick={handleShowMore}>
							{t('show-more-tracks')}
						</Button>
					)}
				</>
			)}
		</Container>
	)
}

export default TrackResults
